import type { App } from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import JwtService from "@/core/services/JwtService";
import type { AxiosResponse } from "axios";

/**
 * @description service to call HTTP request via Axios
 */
class ApiService {
  /**
   * @description property to share vue instance
   */
  public static vueInstance: App;

  /**
   * @description initialize vue axios
   */
  public static init(app: App<Element>) {
    ApiService.vueInstance = app;
    ApiService.vueInstance.use(VueAxios, axios);
  }

  /**
   * @description set the default HTTP request headers
   */
  public static setHeader(): void {
    ApiService.vueInstance.axios.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${JwtService.getToken()}`;
    ApiService.vueInstance.axios.defaults.headers.common["Accept"] =
      "application/json";
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @param type: string
   * @returns Promise<AxiosResponse>
   */
  public static query(
    resource: string,
    params: any,
    type?: string
  ): Promise<AxiosResponse> {
    this.setBaseUrl(type);
    return ApiService.vueInstance.axios.get(resource, params);
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param slug: string
   * @param type: string
   * @returns Promise<AxiosResponse>
   */
  public static get(
    resource: string,
    slug = "" as string,
    params?: {},
    type?
  ): Promise<AxiosResponse> {
    this.setBaseUrl(type);
    return ApiService.vueInstance.axios.get(
      `${resource}${slug ? "/" + slug : ""}`,
      { params }
    );
  }

  /**
   * @description set the POST HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @param type: string
   * @returns Promise<AxiosResponse>
   */
  public static post(
    resource: string,
    params: any,
    type?: string,
    headers?: any
  ): Promise<AxiosResponse> {
    this.setBaseUrl(type);
    return ApiService.vueInstance.axios.post(`${resource}`, params, {
      headers,
    });
  }

  /**
   * @description send the UPDATE HTTP request
   * @param resource: string
   * @param slug: string
   * @param params: AxiosRequestConfig
   * @param type: string
   * @returns Promise<AxiosResponse>
   */
  public static update(
    resource: string,
    slug: string,
    params: any,
    type: string
  ): Promise<AxiosResponse> {
    this.setBaseUrl(type);
    return ApiService.vueInstance.axios.put(`${resource}/${slug}`, params);
  }

  /**
   * @description Send the PUT HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @param type: string
   * @returns Promise<AxiosResponse>
   */
  public static put(
    resource: string,
    params: any,
    type = ""
  ): Promise<AxiosResponse> {
    this.setBaseUrl(type);
    return ApiService.vueInstance.axios.put(`${resource}`, params);
  }

  /**
   * @description Send the DELETE HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @param type: string
   * @returns Promise<AxiosResponse>
   */
  public static delete(
    resource: string,
    params: any,
    type?: string
  ): Promise<AxiosResponse> {
    this.setBaseUrl(type);
    return ApiService.vueInstance.axios.delete(resource, { params: params });
  }

  private static setBaseUrl(type?: string) {
    if (type === "order") {
      ApiService.vueInstance.axios.defaults.baseURL =
        import.meta.env.VITE_APP_ORDER_API_URL;
    } else if (type === "warehouse") {
      ApiService.vueInstance.axios.defaults.baseURL =
        import.meta.env.VITE_APP_WAREHOUSE_API_URL;
    } else if (type === "visitor") {
      ApiService.vueInstance.axios.defaults.baseURL =
        import.meta.env.VITE_APP_VISITOR_API_URL;
    } else {
      ApiService.vueInstance.axios.defaults.baseURL =
        import.meta.env.VITE_APP_ACC_API_URL;
    }
  }
}

export default ApiService;
